import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from './cn';

/**
 * RadioGroup — 단일 선택 라디오 (DESIGN-SPEC §4, §10).
 *
 * @radix-ui/react-radio-group 미설치 → 의존성 추가 없이 **native `input[type=radio]`**
 * 위에 토큰 스타일 인디케이터를 얹는다. 같은 `name` 을 공유하는 native 라디오는
 * 브라우저가 roving tabindex·화살표(↑↓←→) 그룹 이동·form 제출 시맨틱을 기본 제공하므로
 * 접근성을 손으로 재구현할 필요가 없다.
 *
 * 두 가지 사용 형태를 지원한다(둘 다 후방호환):
 *   1) 옵션 배열  — `<RadioGroup value=.. onValueChange=.. options={[{value,label,disabled?}]} />`
 *   2) 합성        — `<RadioGroup value=.. onValueChange=..><RadioGroupItem value=".." >라벨</RadioGroupItem></RadioGroup>`
 *
 * checked = `--accent-default`(브랜드 red) dot. 테두리 `--content-soft`(rest)/`--accent-default`(checked).
 * focus-visible = `--focus-ring-brand`. 컨트롤 높이는 라벨 줄과 정렬되도록 16px dot 기준.
 */

export interface RadioOption {
  value: string;
  label: ReactNode;
  /** 옵션 단위 비활성. */
  disabled?: boolean;
  /** 라벨 아래 보조 설명(선택). */
  description?: ReactNode;
}

type Orientation = 'horizontal' | 'vertical';

export interface RadioGroupProps {
  /** 현재 선택값. */
  value: string | null | undefined;
  /** 선택 변경 콜백. */
  onValueChange: (value: string) => void;
  /**
   * native 라디오 그룹 name. 생략 시 `useId` 로 자동 생성한다(한 폼에 여러 그룹이 있어도 충돌 없음).
   * 동일 name 을 공유해야 화살표 키 그룹 이동이 동작하므로 컨텍스트로 자식에 전파한다.
   */
  name?: string;
  /** 옵션 배열 형태. children 과 동시 사용 시 options 가 먼저 렌더된다. */
  options?: ReadonlyArray<RadioOption>;
  /** 합성 형태 — `RadioGroupItem` 자식. */
  children?: ReactNode;
  /** 그룹 전체 비활성. */
  disabled?: boolean;
  /** 배치 방향. 기본 vertical. */
  orientation?: Orientation;
  /** 접근성 그룹 라벨(radiogroup role). */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
  'data-testid'?: string;
}

interface RadioGroupContextValue {
  name: string;
  value: string | null | undefined;
  onValueChange: (value: string) => void;
  groupDisabled: boolean;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

const useRadioGroupContext = (component: string): RadioGroupContextValue => {
  const ctx = useContext(RadioGroupContext);
  if (!ctx) {
    throw new Error(`${component} 는 <RadioGroup> 안에서만 사용할 수 있습니다.`);
  }
  return ctx;
};

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      value,
      onValueChange,
      name,
      options,
      children,
      disabled = false,
      orientation = 'vertical',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      className,
      'data-testid': testId,
    },
    ref,
  ) => {
    const autoName = useId();
    const groupName = name ?? `radiogroup-${autoName}`;

    return (
      <RadioGroupContext.Provider
        value={{ name: groupName, value, onValueChange, groupDisabled: disabled }}
      >
        <div
          ref={ref}
          role="radiogroup"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          aria-orientation={orientation}
          data-testid={testId}
          className={cn(
            'flex gap-x-5 gap-y-2',
            orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap items-center',
            className,
          )}
        >
          {options?.map((opt) => (
            <RadioGroupItem
              key={opt.value}
              value={opt.value}
              {...(opt.disabled !== undefined ? { disabled: opt.disabled } : {})}
              {...(opt.description !== undefined ? { description: opt.description } : {})}
            >
              {opt.label}
            </RadioGroupItem>
          ))}
          {children}
        </div>
      </RadioGroupContext.Provider>
    );
  },
);
RadioGroup.displayName = 'RadioGroup';

export interface RadioGroupItemProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'type' | 'name' | 'checked' | 'value' | 'onChange' | 'disabled'
  > {
  value: string;
  children: ReactNode;
  disabled?: boolean;
  /** 라벨 아래 보조 설명(선택). */
  description?: ReactNode;
}

/**
 * RadioGroup 안의 단일 옵션. native `input[type=radio]` 을 시각적으로 숨기고
 * (`sr-only` 가 아니라 `peer` 로 두어 접근성 트리·키보드·form 제출은 유지),
 * 토큰 스타일 dot 으로 대체한다. 라벨은 `htmlFor` 로 연결된다.
 */
export const RadioGroupItem = forwardRef<HTMLInputElement, RadioGroupItemProps>(
  ({ value, children, disabled, description, className, id, ...props }, ref) => {
    const { name, value: groupValue, onValueChange, groupDisabled } =
      useRadioGroupContext('RadioGroupItem');
    const autoId = useId();
    const inputId = id ?? `${name}-${autoId}`;
    const isDisabled = disabled || groupDisabled;
    const checked = groupValue === value;

    return (
      <div className={cn('flex items-start gap-2', className)}>
        <span className="relative flex h-5 shrink-0 items-center">
          <input
            ref={ref}
            type="radio"
            id={inputId}
            name={name}
            value={value}
            checked={checked}
            disabled={isDisabled}
            onChange={() => onValueChange(value)}
            className={cn(
              // native input 을 dot 위에 겹쳐 두되 투명 처리 — 클릭/포커스/키보드는 native 가 처리.
              'peer absolute inset-0 m-0 h-4 w-4 cursor-pointer appearance-none rounded-full',
              'disabled:cursor-not-allowed',
            )}
            {...props}
          />
          {/* 시각 인디케이터 — peer-checked/peer-focus-visible 로 상태 반영. */}
          <span
            aria-hidden="true"
            className={cn(
              'flex h-4 w-4 items-center justify-center rounded-full border transition-colors',
              'duration-[var(--motion-fast)]',
              'peer-focus-visible:[box-shadow:var(--focus-ring-brand)]',
              'peer-disabled:opacity-50',
              checked
                ? 'border-[var(--accent-default)] bg-[var(--surface-0)]'
                : 'border-[var(--content-soft)] bg-[var(--surface-0)]',
            )}
          >
            <span
              className={cn(
                'h-2 w-2 rounded-full transition-transform duration-[var(--motion-fast)]',
                checked ? 'scale-100' : 'scale-0',
              )}
              style={{ background: 'var(--accent-default)' }}
            />
          </span>
        </span>
        <label
          htmlFor={inputId}
          className={cn(
            'flex flex-col gap-0.5 text-[13px] leading-5',
            isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          )}
          style={{ color: 'var(--content-strong)' }}
        >
          <span className="font-medium">{children}</span>
          {description ? (
            <span className="text-[12px] font-normal" style={{ color: 'var(--content-muted)' }}>
              {description}
            </span>
          ) : null}
        </label>
      </div>
    );
  },
);
RadioGroupItem.displayName = 'RadioGroupItem';
